import { getIndexPathForRole } from '@helpers/role.functions';
import type { RestrictedRouter } from '@helpers/types/RestrictedRouter';
import keycloakIns, {
  clearChallengeStorage,
  createJoinRegistrationUrl,
  isChallengeBootstrap,
  markChallengeSessionExpired,
} from '@lib/keycloak';
import { HOME_ROUTE_PATH } from '@lib/homePath';
import appointmentRoutes from '@src/router/routes/appointments';
import authRoutes from '@src/router/routes/auth';
import callRoutes from '@src/router/routes/call';
import exerciseRoutes from '@src/router/routes/exercises';
import patientRoutes from '@src/router/routes/patient';
import preventionRoutes from '@src/router/routes/prevention';
import shopRoutes from '@src/router/routes/shop';
import steuerberaterPublicRoutes from '@src/router/routes/steuerberater-public';
import notificationSettingsPublicRoutes from '@src/router/routes/notification-settings-public';
import { requestJoinInviteReview, takeJoinInviteReview } from '@lib/joinInviteReview';
import { getActiveCaregiverId } from '@lib/activeOffice';
import { isCallSessionTab } from '@lib/callSession';
import { useAuthStore } from '@stores/authStore';
import { useIndexStore } from '@stores/index';
import { useModalStore } from '@stores/modalStore';
import { useToastStore } from '@stores/toastStore';
import { useSignupStore } from '@stores/signupStore';
import {
  appendVueRoute,
  FeatureFlagType,
  getPath,
  PayedFeatureType,
  Role,
  type ConcreteRoutePath,
  type RoutePath,
  type RouteType,
} from '@therame/shared';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

export type RestrictedRecordRaw = Omit<RouteRecordRaw, 'name' | 'path'> & {
  name: RouteType;
  path: RoutePath<RouteType>;
};

let routes: Array<RouteRecordRaw> = [
  {
    ...appendVueRoute<'home'>('home', '/'),
    path: HOME_ROUTE_PATH as RoutePath<'home'>,
    component: () => import('@src/views/Home.vue'),
    meta: {
      layout: 'default',
      title: 'Dashboard',
      noPadding: true,
      noBorder: true,
      // The Dashboard lays its own widgets out as separate islands, so the
      // content island itself stays transparent and the grey page background
      // shows through between them.
      transparentContent: true,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [
        Role.ADMIN,
        Role.THERAPIST,
        Role.MANAGEMENT,
        Role.PATIENT,
        Role.PREVENTION,
        Role.SUPERADMIN,
      ],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
      officeLocationScoped: true,
      workspace: true,
      workspaceFeatureFlag: FeatureFlagType.APPOINTMENT_SCHEDULING,
    },
  },
  {
    ...appendVueRoute<'purchaseComplete'>('purchaseComplete', '/purchase-complete'),
    component: () => import('@src/views/Home.vue'),
    props: true,
    meta: {
      layout: 'default',
      title: 'Dashboard',
      noPadding: true,
      noBorder: true,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.PATIENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
    },
  },
  {
    ...appendVueRoute<'help'>('help', '/help'),
    component: () => import('@src/views/Help.vue'),
    meta: {
      layout: 'default',
      title: 'Hilfe',
      noPadding: true,
      requiresAuth: true,
      requiresOnboarding: true,
      noBorder: false,
      // Superadmins match no help content and would render an empty page, so
      // the URL refuses them rather than showing a blank screen.
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT, Role.PATIENT],
    },
  },
  {
    ...appendVueRoute<'developerSettingsWebhooks'>(
      'developerSettingsWebhooks',
      '/developer-settings/webhooks',
    ),
    // Workspace catch-all (ADR-0027 §A / PRD #963): the base path is the
    // empty-cascade case; deeper paths like
    // `/developer-settings/webhooks/webhookSubscriptionDetails:abc` are columns.
    path: '/developer-settings/webhooks/:cascade(.*)?' as RoutePath<'developerSettingsWebhooks'>,
    component: () => import('@views/DeveloperSettingsWebhooks.vue'),
    meta: {
      layout: 'default',
      title: 'Webhooks',
      noPadding: true,
      hideTitle: true,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN],
      accessPossibleWithFeatureFlags: [FeatureFlagType.API_KEYS, FeatureFlagType.WEBHOOKS],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'settingsApiKeys'>('settingsApiKeys', '/settings/api-keys'),
    // Workspace catch-all (ADR-0027 §A / PRD #963): the base path is the
    // empty-cascade case; deeper paths like
    // `/settings/api-keys/apiKeyDetails:abc` are columns.
    path: '/settings/api-keys/:cascade(.*)?' as RoutePath<'settingsApiKeys'>,
    component: () => import('@views/DeveloperSettingsApiKeys.vue'),
    meta: {
      layout: 'default',
      title: 'API Keys',
      noPadding: true,
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN],
      accessPossibleWithFeatureFlags: [FeatureFlagType.API_KEYS, FeatureFlagType.WEBHOOKS],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'agb'>('agb', '/agb'),
    component: () => import('@src/views/AGB.vue'),
    meta: {
      layout: 'basic',
      title: 'AGB',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'challenge'>('challenge', '/challenge'),
    component: () => import('@src/views/ChallengeLanding.vue'),
    meta: {
      layout: 'basic',
      title: 'Therame Challenge',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'calendar'>('calendar', '/calendar'),
    // Workspace catch-all (PRD #963): serializes the open cascade into the URL.
    // `/calendar` is the empty-cascade case; deeper paths like
    // `/calendar/appointmentComposer:new` are inline columns (the Termin-
    // erstellen composer opens here as a column instead of an overlay).
    path: '/calendar/:cascade(.*)?' as RoutePath<'calendar'>,
    components: {
      default: () => import('@views/Calendar.vue'),
      leftSidebar: () => import('@components/Calendar/CalendarSideList.vue'),
    },
    meta: {
      layout: 'default',
      noPadding: true,
      title: 'Kalender',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
      workspace: true,
      officeLocationScoped: true,
      compactColumns: true,
    },
  },
  {
    ...appendVueRoute<'imprint'>('imprint', '/impressum'),
    component: () => import('@src/views/Impressum.vue'),
    meta: {
      layout: 'basic',
      title: 'Impressum',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'privacyPolicy'>('privacyPolicy', '/datenschutz'),
    component: () => import('@src/views/PrivacyPolicy.vue'),
    meta: {
      layout: 'basic',
      title: 'Datenschutzerklärung',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'legal'>('legal', '/legal/:legalDocument'),
    component: () => import('@src/views/Legal.vue'),
    meta: {
      layout: 'basic',
      title: 'Info',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'settingAccounts'>('settingAccounts', '/settings/account'),
    component: () => import('@src/views/SettingsAccountView.vue'),
    meta: {
      layout: 'default',
      title: 'Profil',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [
        Role.ADMIN,
        Role.THERAPIST,
        Role.MANAGEMENT,
        Role.PATIENT,
        Role.PREVENTION,
        Role.SUPERADMIN,
      ],
    },
  },
  {
    ...appendVueRoute<'prescriptions'>('prescriptions', '/prescriptions'),
    // Workspace catch-all: serializes the open Verordnung cascade columns into
    // the URL (PRD #963). The base path `/prescriptions` is the empty-cascade
    // case; deeper paths like `/prescriptions/prescription:abc` are
    // columns. The `prescription` sidebar type is inline-capable, so opening one
    // from the list auto-routes into the cascade instead of an overlay.
    path: '/prescriptions/:cascade(.*)?' as RoutePath<'prescriptions'>,
    component: () => import('@src/views/PrescriptionView.vue'),
    meta: {
      layout: 'default',
      title: 'Verordnungen',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [
        Role.ADMIN,
        Role.THERAPIST,
        Role.MANAGEMENT,
        Role.PATIENT,
        Role.PREVENTION,
        Role.SUPERADMIN,
      ],
      accessPossibleWithFeatureFlags: [FeatureFlagType.PRESCRIPTIONS],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
      workspace: true,
      officeLocationScoped: true,
    },
  },
  {
    ...appendVueRoute<'settingNotifications'>('settingNotifications', '/settings/notifications'),
    component: () => import('@src/views/SettingsNotificationView.vue'),
    meta: {
      layout: 'default',
      title: 'Benachrichtigungen',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [
        Role.ADMIN,
        Role.THERAPIST,
        Role.MANAGEMENT,
        Role.PATIENT,
        Role.PREVENTION,
        Role.SUPERADMIN,
      ],
    },
  },
  {
    ...appendVueRoute<'settingsLegal'>('settingsLegal', '/settings/legal'),
    component: () => import('@src/views/SettingsLegalView.vue'),
    meta: {
      layout: 'default',
      title: 'Rechtliches',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [
        Role.ADMIN,
        Role.THERAPIST,
        Role.MANAGEMENT,
        Role.PATIENT,
        Role.PREVENTION,
        Role.SUPERADMIN,
      ],
    },
  },
  {
    ...appendVueRoute<'settingCustomForms'>('settingCustomForms', '/editors/customforms'),
    // Workspace catch-all (#1111/#1120): the forms list is the workspace root
    // view; opening/creating a form docks the editor (`customFormDetail`) as an
    // inline cascade column serialized into this URL, mirroring
    // `/prescriptions/:cascade(.*)?`. The old standalone create/edit routes are
    // deleted (no redirects). No `officeLocationScoped` — the data is office-scoped.
    path: '/editors/customforms/:cascade(.*)?' as RoutePath<'settingCustomForms'>,
    component: () => import('@src/views/SettingsCustomFormView.vue'),
    meta: {
      layout: 'default',
      title: 'Formulare',
      hideTitle: false,
      requiresAuth: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.THERAPIST, Role.MANAGEMENT],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'officeStaff'>('officeStaff', '/office/staff'),
    // Workspace catch-all: serializes the open staff cascade column into the URL
    // (PRD #963). The base path `/office/staff` is the empty-cascade case; deeper
    // paths like `/office/staff/staffFile:abc` are columns.
    path: '/office/staff/:cascade(.*)?' as RoutePath<'officeStaff'>,
    component: () => import('@src/views/OfficeMgm.vue'),
    meta: {
      layout: 'default',
      title: 'Personal',
      requiresAuth: true,
      hideTitle: false,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.MANAGEMENT],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'officeProfile'>('officeProfile', '/office/profile'),
    components: {
      default: () => import('@src/views/AccountSettingsView.vue'),
      footer: () => import('@components/Settings/SettingsAccountFooter.vue'),
    },
    meta: {
      layout: 'default',
      title: 'Einstellungen',
      requiresAuth: true,
      hideTitle: false,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [
        Role.ADMIN,
        Role.THERAPIST,
        Role.MANAGEMENT,
        Role.PATIENT,
        Role.PREVENTION,
        Role.SUPERADMIN,
      ],
    },
  },
  {
    ...appendVueRoute<'officeSubscription'>('officeSubscription', '/office/subscriptions'),
    // Workspace catch-all: serializes the open cascade into the URL (PRD #963).
    // The base path `/office/subscriptions` is the empty-cascade case; deeper
    // paths like `/office/subscriptions/officeLocationSettings:abc` are columns.
    path: '/office/subscriptions/:cascade(.*)?' as RoutePath<'officeSubscription'>,
    component: () => import('@src/views/OfficeLocationsSubscriptions.vue'),
    meta: {
      layout: 'default',
      title: 'Standorte',
      requiresAuth: true,
      hideTitle: false,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.MANAGEMENT],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'officeShop'>('officeShop', '/office-shop'),
    // Workspace catch-all (ADR-0027 §A / PRD #963): the base path `/office-shop`
    // is the empty-cascade case; deeper paths like
    // `/office-shop/officeProductDetails:abc` are columns.
    path: '/office-shop/:cascade(.*)?' as RoutePath<'officeShop'>,
    components: {
      default: () => import('/src/views/OfficeShop.vue'),
      // "Rechnungen" as its own island below the stats + Leistungen island.
      footer: () => import('@components/OfficeShop/OfficeShopInvoicesFooter.vue'),
    },
    meta: {
      layout: 'default',
      title: 'Shop',
      requiresAuth: true,
      hideTitle: false,
      requiresOnboarding: true,
      noPadding: true,
      noBorder: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.MANAGEMENT],
      workspace: true,
      officeLocationScoped: true,
    },
  },
  {
    ...appendVueRoute<'officeInvoices'>('officeInvoices', '/office/invoices'),
    // Workspace catch-all: serializes the open invoice cascade column into the URL
    // (PRD #963). The base path `/office/invoices` is the empty-cascade case; deeper
    // paths like `/office/invoices/patientInvoiceDetail:abc` are columns.
    path: '/office/invoices/:cascade(.*)?' as RoutePath<'officeInvoices'>,
    component: () => import('@src/views/OfficeInvoices.vue'),
    meta: {
      layout: 'default',
      title: 'Rechnungen',
      requiresAuth: true,
      hideTitle: false,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.MANAGEMENT],
      workspace: true,
      officeLocationScoped: true,
    },
  },
  {
    ...appendVueRoute<'integrationsOverview'>('integrationsOverview', '/integrations-overview'),
    component: () => import('/src/views/IntegrationsOverview.vue'),
    meta: {
      layout: 'default',
      title: 'Schnittstellen',
      requiresAuth: true,
      hideTitle: true,
      requiresOnboarding: true,
      noPadding: true,
      accessPossibleWithRoles: [Role.ADMIN, Role.MANAGEMENT],
      accessPossibleWithFeatureFlags: [FeatureFlagType.INTEGRATIONS],
    },
  },
  {
    ...appendVueRoute<'diagnosis'>('diagnosis', '/diagnosis'),
    component: () => import('@src/views/Diagnosis.vue'),
    meta: {
      layout: 'default',
      title: 'Fragebogen',
      requiresAuth: true,
      hideTitle: false,
      requiresOnboarding: true,
      noPadding: true,
      // The page lays its two foldouts out as separate islands (Figma), so the
      // content island itself stays transparent and the grey page background
      // shows through between them — same mechanism as the Dashboard.
      transparentContent: true,
      accessPossibleWithRoles: [Role.PATIENT],
      accessPossibleWithPayedFeatures: [PayedFeatureType.THERAME],
      // No `workspace: true` (PR6 — TM-9295): patients get no cascade columns
      // at all, only sidebars, so `shouldRouteToCascade` (sidebarStore.ts)
      // must see this route as non-workspace. `customFormPlayer`/`anamnesisResults`
      // still have `inline` configs (for PR7's therapist-side column), but
      // that branch only fires when the CURRENT route is a workspace one —
      // it never will be here.
    },
  },
  {
    ...appendVueRoute<'diagnosisExternal'>('diagnosisExternal', '/diagnosis-external/:token'),
    component: () => import('@src/views/DiagnosisExternal.vue'),
    meta: {
      layout: 'none',
      title: 'Fragebogen',
      requiresAuth: false,
      hideTitle: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'diagnosisExternalRegistration'>(
      'diagnosisExternalRegistration',
      '/diagnosis-external/:token',
    ),
    component: () => import('@src/views/DiagnosisExternal.vue'),
    meta: {
      layout: 'none',
      title: 'Fragebogen',
      requiresAuth: false,
      hideTitle: false,
      requiresOnboarding: false,
    },
  },
  {
    // §302 Versand (TM-9635 / #1612): the Versand-Operator's pages, in the
    // default layout with their own navigation group (menuItemStore). The account
    // has no office and no location, so the location picker stays empty and
    // invisible. `requiresOnboarding` is off, because the operator goes through
    // no signup path; the role gets its account by hand in the Keycloak admin
    // console.
    ...appendVueRoute<'versand'>('versand', '/versand'),
    redirect: getPath<'versandIdentity'>('/versand/identitaet', {}),
  },
  {
    ...appendVueRoute<'versandIdentity'>('versandIdentity', '/versand/identitaet'),
    component: () => import('@src/views/VersandIdentity.vue'),
    meta: {
      layout: 'default',
      title: 'Identität prüfen',
      requiresAuth: true,
      requiresOnboarding: false,
      accessPossibleWithRoles: [Role.TRANSMISSION_OPERATOR],
    },
  },
  {
    ...appendVueRoute<'versandLieferungen'>('versandLieferungen', '/versand/lieferungen'),
    // Workspace catch-all (ADR-0027 §A): the detail column of a Lieferung is in
    // the URL — `/versand/lieferungen/versandLieferungDetail:<id>` — so that the
    // operator can copy it into a ticket.
    path: '/versand/lieferungen/:cascade(.*)?' as RoutePath<'versandLieferungen'>,
    component: () => import('@src/views/VersandLieferungen.vue'),
    meta: {
      layout: 'default',
      title: 'Lieferungen',
      requiresAuth: true,
      requiresOnboarding: false,
      accessPossibleWithRoles: [Role.TRANSMISSION_OPERATOR],
      workspace: true,
    },
  },
  {
    ...appendVueRoute<'versandRueckmeldungen'>('versandRueckmeldungen', '/versand/rueckmeldungen'),
    component: () => import('@src/views/VersandRueckmeldungen.vue'),
    meta: {
      layout: 'default',
      title: 'Rückmeldungen',
      requiresAuth: true,
      requiresOnboarding: false,
      accessPossibleWithRoles: [Role.TRANSMISSION_OPERATOR],
    },
  },
  {
    ...appendVueRoute<'superadminList'>('superadminList', '/superadmin/list'),
    component: () => import('@src/views/SuperAdminList.vue'),
    meta: {
      layout: 'default',
      title: 'Accountmanagement',
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.SUPERADMIN],
    },
  },
  {
    ...appendVueRoute<'superadminDetails'>('superadminDetails', '/superadmin/:officeId'),
    component: () => import('@src/views/SuperAdminDetails.vue'),
    meta: {
      layout: 'default',
      title: 'Accountmanagement',
      requiresAuth: true,
      requiresOnboarding: true,
      accessPossibleWithRoles: [Role.SUPERADMIN],
    },
  },
  {
    ...appendVueRoute<'emoji'>('emoji', '/emoji'),
    component: () => import('@src/components/VideoCall/VideoCallEmojiPicker.vue'),
    meta: {
      layout: 'default',
      title: 'emoji',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'connectionTest'>('connectionTest', '/verbindungstest'),
    component: () => import('@src/views/VideoCallTestExternal.vue'),
    meta: {
      layout: 'none',
      title: 'Verbindungstest',
      requiresAuth: false,
      requiresOnboarding: false,
    },
  },
  {
    ...appendVueRoute<'genericError'>('genericError', '/error'),
    component: () => import('@src/views/GenericError.vue'),
    meta: {
      layout: 'none',
      title: 'Error',
      requiresAuth: false,
      requiresOnboarding: false,
      hideTitle: true,
    },
  },
  {
    ...appendVueRoute<'noServer'>('noServer', '/502'),
    component: () => import('@src/views/NoServer.vue'),
    meta: {
      layout: 'none',
      title: 'Error 502',
      requiresAuth: false,
      requiresOnboarding: false,
      hideTitle: true,
    },
  },
  {
    ...appendVueRoute<'notFound'>('notFound', '/404'),
    component: () => import('@src/views/NotFound.vue'),
    meta: {
      layout: 'none',
      title: 'Error 404',
      requiresAuth: false,
      requiresOnboarding: false,
      hideTitle: true,
    },
  },
  {
    ...appendVueRoute<'noAccess'>('noAccess', '/403'),
    component: () => import('@src/views/NoAccess.vue'),
    meta: {
      layout: 'none',
      title: 'Error 403',
      requiresAuth: false,
      requiresOnboarding: false,
      accessPossibleWithTan: true,

      hideTitle: true,
    },
  },

  //...preventionRoutes,
] satisfies RestrictedRecordRaw[];

routes = routes.concat(appointmentRoutes);
routes = routes.concat(authRoutes);
routes = routes.concat(callRoutes);
routes = routes.concat(exerciseRoutes);
routes = routes.concat(patientRoutes);
routes = routes.concat(preventionRoutes);
routes = routes.concat(shopRoutes);
routes = routes.concat(steuerberaterPublicRoutes);
routes = routes.concat(notificationSettingsPublicRoutes);

routes.push({
  ...appendVueRoute<'catchall'>('catchall', '/:catchAll(.*)'),
  redirect: '/404',
});

const isDev = import.meta.env.VITE_MODE === 'LOCAL';
if (isDev) {
  routes.push({
    ...appendVueRoute<'iconList'>('iconList', '/icons'),
    component: () => import('@views/DevOnlyIconList.vue'),
    meta: {
      layout: 'none',
      title: 'Icon List',
      requiresAuth: false,
      requiresOnboarding: false,
      hideTitle: true,
    },
  });
}
const isDevOrTest = ['LOCAL', 'TESTING-FRONTEND'].includes(import.meta.env.VITE_MODE);
if (isDevOrTest) {
  routes.push({
    ...appendVueRoute<'fixtures'>('fixtures', '/fixtures'),
    component: () => import('@views/DevOnlyFixtures.vue'),
    meta: {
      layout: 'none',
      title: 'Fixtures',
      requiresAuth: false,
      requiresOnboarding: false,
      hideTitle: true,
    },
  });
}
const router = createRouter({
  history: createWebHistory(),
  routes,
}) as RestrictedRouter;
router.beforeEach(async (to, from, next) => {
  const signupStore = useSignupStore();
  const authStore = useAuthStore();
  const indexStore = useIndexStore();

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresOnboarding = to.matched.some((record) => record.meta.requiresOnboarding);

  // The join mail's link (#1825): ask for the acceptance modal and carry on as
  // after any login, in the tab's office. It opens once a page of the app is up.
  if (to.name === 'joinInvite') {
    requestJoinInviteReview();
    if (keycloakIns.authenticated) {
      next({ path: '/' });
    } else {
      await keycloakIns.login({ redirectUri: `${window.location.origin}/` });
    }
    return;
  }

  // A registration link's QR code (#1834): a visitor without a session
  // registers there, or logs in from there, and comes back to the join page.
  if (to.name === 'joinOffice' && !keycloakIns.authenticated && !isCallSessionTab()) {
    window.location.assign(await createJoinRegistrationUrl(String(to.params.token)));
    return;
  }

  // „Praxis eröffnen“ and joining through a registration link need an Account,
  // not a Person (#1832, #1834); a TAN session is none.
  const opensOffice = to.name === 'openOffice' && authStore.authContext?.usedForm !== 'tan';
  const joinsOffice = to.name === 'joinOffice' && authStore.authContext?.usedForm !== 'tan';

  // No Person the session could act as: „Praxis eröffnen“ or „Abmelden“ (#1832).
  // An office signup goes straight into „Praxis eröffnen“, from any device,
  // until it has opened its office (#1833); its „Abbrechen“ leads here. A
  // patient who registered through a link goes to its join page (#1834).
  if (authStore.accountWithoutOffice && keycloakIns.authenticated) {
    if (to.name === 'accountWithoutOffice' || opensOffice || joinsOffice) {
      next();
    } else if (authStore.registrationRole === Role.ADMIN) {
      next({ path: getPath<'openOffice'>('/praxis-eroeffnen', {}) });
    } else if (authStore.registrationRole === Role.PATIENT && authStore.registrationLinkToken) {
      next({
        path: getPath<'joinOffice'>('/join/:token', { token: authStore.registrationLinkToken }),
      });
    } else {
      next({ path: getPath<'accountWithoutOffice'>('/ohne-praxis', {}) });
    }
    return;
  }
  if (to.name === 'accountWithoutOffice') {
    next({ path: '/' });
    return;
  }

  // Several offices and no valid stored choice: nothing but „Praxis wählen“
  // (#1822), which links to „Praxis eröffnen“.
  if (authStore.officeChoicePending && keycloakIns.authenticated) {
    if (to.name === 'officePicker' || opensOffice || joinsOffice) {
      next();
    } else {
      next({ path: getPath<'officePicker'>('/praxis-waehlen', {}) });
    }
    return;
  }
  if (to.name === 'officePicker' && !authStore.officeChoicePending) {
    next({ path: '/' });
    return;
  }

  // Legal acceptances come before anything else, per Person of the Active
  // office (#1824). Keycloak no longer asks at login.
  if (authStore.pendingLegalDocuments.length > 0 && keycloakIns.authenticated) {
    if (to.name === 'legalAcceptance' || joinsOffice) {
      next();
    } else {
      next({ path: getPath<'legalAcceptance'>('/zustimmung', {}) });
    }
    return;
  }
  if (to.name === 'legalAcceptance') {
    next({ path: '/' });
    return;
  }

  // Redirect to superadmin list if superadmin logs in
  if (to.name === 'home' && authStore.authContext?.user?.role === Role.SUPERADMIN) {
    router.push(getPath<'superadminList'>('/superadmin/list', {}));
    return;
  }

  // A TAN session reaches its call and nothing else (#1856): the backend
  // refuses a call token everywhere but at the call's own endpoints.
  if (!to.meta.accessPossibleWithTan && authStore.authContext?.usedForm === 'tan') {
    if (!authStore.authContext?.tanCallId) {
      next({ path: getPath<'noAccess'>('/403', {}) });
      return;
    }

    next({
      path: getPath<'videoCallPre'>('/consultation/pre/:callId', {
        callId: authStore.authContext?.tanCallId,
      }),
    });
    return;
  }

  // Only the patient joins a call, never a caregiver acting for them: a link
  // into a call (an old mail, a bookmark) lands on the start page.
  if (to.meta.joinsCall && getActiveCaregiverId() !== null) {
    next({ path: '/' });
    return;
  }

  if (
    authStore.authContext?.user &&
    authStore.authContext?.usedForm !== 'tan' &&
    to.meta.requiresOnboarding &&
    !authStore.subjectOnboarded()
  ) {
    signupStore.chooseSignupScenario(authStore.authContext?.user);
    router.push(getPath<'signup'>('/signup', {}));
    return;
  }

  indexStore.loadingRoute = true;

  if (!requiresAuth) {
    next();
    return;
  }

  // A TAN tab (#1856) holds a call token instead of a Keycloak session.
  if (!keycloakIns.authenticated && !isCallSessionTab()) {
    // Challenge participants run under the direct-grant-only `therame-challenge`
    // client, which has standard flow disabled. Calling keycloakIns.login() here
    // would issue a response_type=code redirect that Keycloak rejects with
    // "Standard flow is disabled for the client", leaving the user on a white
    // screen. Their only valid recovery is re-entering the access code, so route
    // them back to the challenge landing instead of attempting an SSO login.
    if (isChallengeBootstrap) {
      // Clear the stale `challenge_*` keys (especially `challenge_active`) so the
      // next full page load boots the normal `therame-frontend` client instead
      // of re-bootstrapping the dead challenge session.
      clearChallengeStorage();

      // The keycloak client is fixed at module load from `challenge_active`, so
      // there is no SPA escape from challenge mode — only a full reload can swap
      // `therame-challenge` for `therame-frontend`. If the visitor is
      // deliberately leaving the challenge landing (e.g. the logo/login link in
      // LayoutBasic), hard-navigate to the destination so it re-bootstraps as
      // the normal client and reaches the real Keycloak login. Otherwise this is
      // a mid-flow expired/invalid session, so send them back to re-enter the
      // access code (the direct-grant-only client can't do an SSO redirect).
      if (from.path === getPath<'challenge'>('/challenge', {})) {
        window.location.assign(to.fullPath);
        return;
      }

      // One-shot expired signal (sessionStorage, consumed on read by the
      // landing) rather than a sticky `?expired=1` query param.
      markChallengeSessionExpired();
      next({ path: getPath<'challenge'>('/challenge', {}) });
      return;
    }

    const idpHint = to.query.idpHint as string | undefined;

    await keycloakIns.login({
      idpHint,
    });
    next();
    return;
  }

  if (!requiresOnboarding) {
    next();
    return;
  }

  if (
    to.meta.accessPossibleWithFeatureFlags &&
    !to.meta.accessPossibleWithFeatureFlags.some((featureFlag) =>
      authStore.isFeatureFlagEnabled(featureFlag),
    )
  ) {
    next({ path: getPath<'noAccess'>('/403', {}) });
    return;
  }

  if (authStore.authContext?.user?.role) {
    const roleMatches = to.matched.some(
      (record) =>
        authStore.authContext?.user?.role !== undefined &&
        record.meta.accessPossibleWithRoles?.includes(authStore.authContext?.user?.role),
    );
    if (roleMatches || !authStore.authContext?.user?.role) {
      next();
    } else {
      next({ path: getIndexPathForRole(authStore.authContext?.user?.role) });
    }
  } else {
    router.push(getPath<'noServer'>('/502', {}));
  }
});

router.afterEach((to) => {
  const indexStore = useIndexStore();

  indexStore.loadingRoute = false;

  // An Account without any office past its gates is on its own page (#1832);
  // an invite may be just what it waits for.
  const pastGates = to.meta.requiresOnboarding || to.name === 'accountWithoutOffice';
  if (pastGates && keycloakIns.authenticated && takeJoinInviteReview()) {
    void useModalStore().openModal({ type: 'joinInvite', props: {} });
  }

  if (to.query.emailChanged === 'true') {
    const toastStore = useToastStore();
    toastStore.addToast({
      text: 'Deine E-Mail-Adresse wurde erfolgreich geändert.',
      type: 'success',
    });
    // Remove query param from URL without triggering navigation
    router.replace({
      path: to.path as ConcreteRoutePath,
      query: { ...to.query, emailChanged: undefined },
    });
  }
});

// Late-bind the global router into a const handle so stores (which can't
// import directly from the router layer per boundaries) can resolve route
// meta from arbitrary callers without relying on a live setup context.
import { registerRouter } from '@/const/routerHandle';
import type { Router } from 'vue-router';
registerRouter(router as unknown as Router);

export default router;
